import { createSlice } from '@reduxjs/toolkit'
import { mockPurchaseOrderData } from '../components/PurchaseOrders/utils/mockPurchaseOrderData'

const purchaseOrderSlice = createSlice({
  name: 'purchaseOrders',
  initialState: {
    orders: mockPurchaseOrderData,
    loading: false,
    error: null,
    selectedOrder: null,
    filteredOrders: mockPurchaseOrderData,
    searchTerm: '',
    statusFilter: null,
    supplierFilter: null,
    departmentFilter: null,
    dateRange: null
  },
  reducers: {
    // Basic CRUD operations
    setOrders: (state, action) => {
      state.orders = action.payload
      state.filteredOrders = action.payload
    },
    addOrder: (state, action) => {
      state.orders.push(action.payload)
      state.filteredOrders = [...state.orders]
    },
    updateOrder: (state, action) => {
      const index = state.orders.findIndex((order) => order.id === action.payload.id)
      if (index !== -1) {
        state.orders[index] = action.payload
        state.filteredOrders = [...state.orders]

        // If the updated order is the selected order, update that too
        if (state.selectedOrder && state.selectedOrder.id === action.payload.id) {
          state.selectedOrder = action.payload
        }
      }
    },
    deleteOrder: (state, action) => {
      state.orders = state.orders.filter((order) => order.id !== action.payload)
      state.filteredOrders = [...state.orders]

      // If the deleted order is the selected order, clear selection
      if (state.selectedOrder && state.selectedOrder.id === action.payload) {
        state.selectedOrder = null
      }
    },

    // Order item operations
    addOrderItem: (state, action) => {
      const { orderId, item } = action.payload
      const orderIndex = state.orders.findIndex((order) => order.id === orderId)

      if (orderIndex !== -1) {
        // Add the item to the order
        state.orders[orderIndex].itemsOrdered.push(item)

        // Recalculate the total cost
        state.orders[orderIndex].totalCost = state.orders[orderIndex].itemsOrdered.reduce(
          (total, item) => total + item.quantity * item.unitCost,
          0
        )

        // Update filtered orders and selected order if necessary
        state.filteredOrders = [...state.orders]
        if (state.selectedOrder && state.selectedOrder.id === orderId) {
          state.selectedOrder = state.orders[orderIndex]
        }
      }
    },
    updateOrderItem: (state, action) => {
      const { orderId, itemId, updates } = action.payload
      const orderIndex = state.orders.findIndex((order) => order.id === orderId)

      if (orderIndex !== -1) {
        const order = state.orders[orderIndex]
        const itemIndex = order.itemsOrdered.findIndex((item) => item.itemId === itemId)

        if (itemIndex !== -1) {
          // Update the item
          order.itemsOrdered[itemIndex] = {
            ...order.itemsOrdered[itemIndex],
            ...updates,
            totalCost: updates.quantity
              ? updates.quantity * (updates.unitCost || order.itemsOrdered[itemIndex].unitCost)
              : order.itemsOrdered[itemIndex].quantity * (updates.unitCost || order.itemsOrdered[itemIndex].unitCost)
          }

          // Recalculate the total cost
          order.totalCost = order.itemsOrdered.reduce((total, item) => total + item.quantity * item.unitCost, 0)

          // Update filtered orders and selected order if necessary
          state.filteredOrders = [...state.orders]
          if (state.selectedOrder && state.selectedOrder.id === orderId) {
            state.selectedOrder = order
          }
        }
      }
    },
    removeOrderItem: (state, action) => {
      const { orderId, itemId } = action.payload
      const orderIndex = state.orders.findIndex((order) => order.id === orderId)

      if (orderIndex !== -1) {
        const order = state.orders[orderIndex]

        // Remove the item
        order.itemsOrdered = order.itemsOrdered.filter((item) => item.itemId !== itemId)

        // Recalculate the total cost
        order.totalCost = order.itemsOrdered.reduce((total, item) => total + item.quantity * item.unitCost, 0)

        // Update filtered orders and selected order if necessary
        state.filteredOrders = [...state.orders]
        if (state.selectedOrder && state.selectedOrder.id === orderId) {
          state.selectedOrder = order
        }
      }
    },

    // Selection state
    selectOrder: (state, action) => {
      state.selectedOrder = action.payload
    },
    clearSelection: (state) => {
      state.selectedOrder = null
    },

    // Loading and error states
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },

    // Filtering and search
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload
      state.filteredOrders = filterOrders(state.orders, state)
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload
      state.filteredOrders = filterOrders(state.orders, state)
    },
    setSupplierFilter: (state, action) => {
      state.supplierFilter = action.payload
      state.filteredOrders = filterOrders(state.orders, state)
    },
    setDepartmentFilter: (state, action) => {
      state.departmentFilter = action.payload
      state.filteredOrders = filterOrders(state.orders, state)
    },
    setDateRange: (state, action) => {
      state.dateRange = action.payload
      state.filteredOrders = filterOrders(state.orders, state)
    },
    clearFilters: (state) => {
      state.searchTerm = ''
      state.statusFilter = null
      state.supplierFilter = null
      state.departmentFilter = null
      state.dateRange = null
      state.filteredOrders = [...state.orders]
    },

    // Order status operations
    updateOrderStatus: (state, action) => {
      const { orderId, status } = action.payload
      const orderIndex = state.orders.findIndex((order) => order.id === orderId)

      if (orderIndex !== -1) {
        state.orders[orderIndex].status = status

        // If status is "Received", set receivedDate to today
        if (status === 'Received' && !state.orders[orderIndex].receivedDate) {
          state.orders[orderIndex].receivedDate = new Date().toISOString().split('T')[0]
        }

        // Update filtered orders and selected order if necessary
        state.filteredOrders = [...state.orders]
        if (state.selectedOrder && state.selectedOrder.id === orderId) {
          state.selectedOrder = state.orders[orderIndex]
        }
      }
    },

    // Approval operations
    addApproval: (state, action) => {
      const { orderId, approval } = action.payload
      const orderIndex = state.orders.findIndex((order) => order.id === orderId)

      if (orderIndex !== -1) {
        // Add the approval to the chain
        state.orders[orderIndex].approvalChain.push(approval)

        // Check if all approvals are complete and update status if needed
        const allApproved = state.orders[orderIndex].approvalChain.every((approval) => approval.status === 'Approved')

        if (allApproved && state.orders[orderIndex].approvalChain.length > 0) {
          state.orders[orderIndex].status = 'Approved'
        }

        // Update filtered orders and selected order if necessary
        state.filteredOrders = [...state.orders]
        if (state.selectedOrder && state.selectedOrder.id === orderId) {
          state.selectedOrder = state.orders[orderIndex]
        }
      }
    },
    updateApproval: (state, action) => {
      const { orderId, approverId, status, timestamp } = action.payload
      const orderIndex = state.orders.findIndex((order) => order.id === orderId)

      if (orderIndex !== -1) {
        const approvalIndex = state.orders[orderIndex].approvalChain.findIndex(
          (approval) => approval.approverId === approverId
        )

        if (approvalIndex !== -1) {
          // Update the approval
          state.orders[orderIndex].approvalChain[approvalIndex] = {
            ...state.orders[orderIndex].approvalChain[approvalIndex],
            status,
            timestamp: timestamp || new Date().toISOString()
          }

          // Check if all approvals are complete and update status if needed
          const allApproved = state.orders[orderIndex].approvalChain.every((approval) => approval.status === 'Approved')

          if (allApproved && state.orders[orderIndex].approvalChain.length > 0) {
            state.orders[orderIndex].status = 'Approved'
          }

          // Update filtered orders and selected order if necessary
          state.filteredOrders = [...state.orders]
          if (state.selectedOrder && state.selectedOrder.id === orderId) {
            state.selectedOrder = state.orders[orderIndex]
          }
        }
      }
    }
  }
})

// Helper function for filtering orders based on multiple criteria
const filterOrders = (orders, state) => {
  const { searchTerm, statusFilter, supplierFilter, departmentFilter, dateRange } = state

  return orders.filter((order) => {
    // Search term filter (across multiple fields)
    const matchesSearch =
      !searchTerm ||
      (order.id && order.id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.supplierName && order.supplierName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.notes && order.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
      // Search in items ordered
      order.itemsOrdered.some((item) => item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase()))

    // Status filter
    const matchesStatus = !statusFilter || order.status === statusFilter

    // Supplier filter
    const matchesSupplier = !supplierFilter || order.supplierId === supplierFilter

    // Department filter
    const matchesDepartment = !departmentFilter || order.department === departmentFilter

    // Date range filter
    const matchesDateRange =
      !dateRange ||
      (order.orderDate &&
        new Date(order.orderDate) >= new Date(dateRange[0]) &&
        new Date(order.orderDate) <= new Date(dateRange[1]))

    return matchesSearch && matchesStatus && matchesSupplier && matchesDepartment && matchesDateRange
  })
}

export const {
  setOrders,
  addOrder,
  updateOrder,
  deleteOrder,
  addOrderItem,
  updateOrderItem,
  removeOrderItem,
  selectOrder,
  clearSelection,
  setLoading,
  setError,
  setSearchTerm,
  setStatusFilter,
  setSupplierFilter,
  setDepartmentFilter,
  setDateRange,
  clearFilters,
  updateOrderStatus,
  addApproval,
  updateApproval
} = purchaseOrderSlice.actions

export default purchaseOrderSlice.reducer
